import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, getToken } from "@/lib/api";
import { EmptyState } from "@/components/Bits";
import HigherDropsAdmin from "@/components/admin/HigherDropsAdmin";
import HigherPaymentsAdmin from "@/components/admin/HigherPaymentsAdmin";
import HigherDomsAdmin from "@/components/admin/HigherDomsAdmin";
import { ShipTag } from "@/components/HigherShipTracker";

const HigherRow = ({ j, i }) => (
  <Link
    to={`/higher/${j.id}`}
    data-testid={`higher-joiner-row-${j.id}`}
    style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}
    className="rise group mb-2.5 flex break-inside-avoid items-center gap-3 rounded-2xl border border-white/5 bg-zinc-900/60 px-3 sm:px-4 py-3.5 transition-colors duration-200 hover:border-sky-400/40 hover:bg-zinc-900"
  >
    <span className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
      <span className="min-w-0 font-semibold truncate">{j.name}</span>
      {j.past_due && <span data-testid={`higher-joiner-past-due-tag-${j.id}`} className="self-start sm:self-auto shrink-0 rounded-full bg-red-500/15 border border-red-500/40 px-2 py-0.5 text-[11px] font-bold text-red-300">Past due</span>}
      {j.due_soon && <span data-testid={`higher-joiner-due-soon-tag-${j.id}`} className="self-start sm:self-auto shrink-0 rounded-full bg-amber-400/15 border border-amber-400/40 px-2 py-0.5 text-[11px] font-bold text-amber-200">Due soon</span>}
      <ShipTag status={j.ship_status} testId={`higher-joiner-ship-tag-${j.id}`} />
    </span>
    {j.total_pcs > 0 && <span className="hidden sm:inline font-mono text-xs text-zinc-500">{j.total_pcs} PCs</span>}
    <ChevronRight className="h-4 w-4 shrink-0 text-zinc-600 transition-transform duration-200 group-hover:translate-x-1" />
  </Link>
);

export default function HigherClaims() {
  const isAdmin = !!getToken();
  const [joiners, setJoiners] = useState(null);
  const [drops, setDrops] = useState([]);
  const [q, setQ] = useState("");
  const [ver, setVer] = useState(0);
  useEffect(() => {
    api.get("/higher/joiners").then((r) => setJoiners(r.data)).catch(() => setJoiners([]));
    if (isAdmin) api.get("/higher/drops").then((r) => setDrops(r.data)).catch(() => setDrops([]));
  }, [isAdmin, ver]);
  useEffect(() => {
    if (isAdmin && joiners && window.location.hash === "#ems-payments") document.getElementById("ems-payments")?.scrollIntoView({ behavior: "smooth" });
  }, [isAdmin, joiners]);
  const filtered = useMemo(() => (joiners || []).filter((j) => j.name.toLowerCase().includes(q.toLowerCase())), [joiners, q]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end gap-4 justify-between">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Higher Store Claims</h1>
          <p className="text-zinc-400 mt-2 text-sm md:text-base">Tap your name to see your Higher PCs and EMS.</p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input data-testid="higher-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your name" className="pl-9 h-11 rounded-xl bg-zinc-900 border-zinc-800" />
        </div>
      </div>
      <div className="mt-8 columns-2 gap-2.5 sm:gap-4" data-testid="higher-joiners-list">
        {joiners === null && <div className="text-zinc-500 text-sm">Loading…</div>}
        {filtered.map((j, i) => <HigherRow key={j.id} j={j} i={i} />)}
      </div>
      {joiners && filtered.length === 0 && (
        <EmptyState testId="higher-joiners-empty" title={joiners.length ? "No match" : "No Higher Joiners yet"} hint={joiners.length ? "Try a different spelling." : "Admins add Higher Joiners from the admin panel."} />
      )}
      {isAdmin && <HigherPaymentsAdmin refreshKey={ver} onChanged={() => setVer((v) => v + 1)} />}
      {isAdmin && <HigherDomsAdmin refreshKey={ver} onChanged={() => setVer((v) => v + 1)} />}
      {isAdmin && <HigherDropsAdmin drops={drops} onChanged={() => setVer((v) => v + 1)} />}
    </div>
  );
}
